/* eslint-disable @typescript-eslint/no-explicit-any */
import useRequest from "@/lib/request/useRequest";
import { Plot } from "@/types/plot";
import { AsAppliedPlot } from "@/types/asAppliedPlot";
import {
    Button,
    Checkbox,
    getKeyValue,
    Slider,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Input,
} from "@heroui/react";
import { useParams } from "@tanstack/react-router";
import dayjs, { Dayjs } from "dayjs";
import { Feature, FeatureCollection, MultiPolygon } from "geojson";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { PlotType } from "@/types/enums";
import useMap from "@/lib/map/useMap";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { ImageSource, RasterLayerSpecification } from "mapbox-gl";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { APPLICATION_OPERATION_IMAGE_LAYER } from "@/lib/map/layers";
import {
    removeLayersIfExists,
    removeSourcesIfExists,
    updateLayerOpacity,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
} from "@/lib/utils/map";
import { faSave, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import useAuth from "@/lib/auth/useAuth";
import { useI18n } from "@/lib/i18n/useI18n";

type PlotTableRow = {
    pairId: number;
    referencePlot?: Feature<MultiPolygon, Plot>;
    canaryPlot?: Feature<MultiPolygon, Plot>;
};

type PlotSetRow = {
    prescriptionId?: number;
    createdDate?: Dayjs;
    latestAppliedDate?: Dayjs;
    latestApplicationId?: number;
    activePlots: number;
    totalPlots: number;
    isSlices?: boolean;
    isSelected?: boolean;
    prescriptionName?: string;
    plots: Feature<MultiPolygon, Plot>[];
};

function groupPlotsByPrescription(
    data?: FeatureCollection<MultiPolygon, Plot>,
) {
    if (!data) return [];
    return data?.features.reduce(
        (acc: PlotSetRow[], plot: Feature<MultiPolygon, Plot>) => {
            let existingRow = acc.find(
                (row) => row.prescriptionId === plot.properties.prescription,
            );
            if (existingRow) {
                existingRow = {
                    ...existingRow,
                    activePlots:
                        existingRow.activePlots +
                        (plot.properties.is_active ? 1 : 0),
                    totalPlots: existingRow.totalPlots + 1,
                    plots: [...existingRow.plots, plot],
                };
            } else {
                existingRow = {
                    prescriptionId: plot.properties.prescription,
                    latestAppliedDate: dayjs(
                        plot.properties.latest_applied_date,
                    ),
                    latestApplicationId: plot.properties
                        .latest_application_id as number,
                    activePlots: plot.properties.is_active ? 1 : 0,
                    totalPlots: 1,
                    isSlices: !!plot.properties.start_degree,
                    prescriptionName: plot.properties
                        .prescription_name as string,
                    plots: [plot],
                    createdDate: dayjs(plot.properties.created_date),
                };
            }
            const newList = acc.filter(
                (row) => row.prescriptionId !== existingRow?.prescriptionId,
            );
            newList.push(existingRow as PlotSetRow);
            return newList;
        },
        [],
    );
}

function groupPlotsByCreatedDate(data?: FeatureCollection<MultiPolygon, Plot>) {
    if (!data) return [];
    return data.features.reduce(
        (acc: PlotSetRow[], plot: Feature<MultiPolygon, Plot>) => {
            const createdDate = dayjs(plot.properties.created_date);

            let existingRow = acc.find((row) =>
                createdDate.isSame(row.createdDate, "minute"),
            );

            if (existingRow) {
                existingRow = {
                    ...existingRow,
                    activePlots:
                        existingRow.activePlots +
                        (plot.properties.is_active ? 1 : 0),
                    totalPlots: existingRow.totalPlots + 1,
                    plots: [...existingRow.plots, plot],
                };
            } else {
                existingRow = {
                    createdDate: createdDate,
                    activePlots: plot.properties.is_active ? 1 : 0,
                    totalPlots: 1,
                    isSlices: !!plot.properties.start_degree,
                    plots: [plot],
                    isSelected: false,
                };
            }

            const newList = acc.filter(
                (row) =>
                    !dayjs(row.createdDate).isSame(
                        dayjs(existingRow?.createdDate),
                        "minute",
                    ),
            );
            newList.push(existingRow as PlotSetRow);
            return newList;
        },
        [],
    );
}

const DRAW_STYLES = [
    ...MapboxDraw.lib.theme,
    {
        id: "plot-fill",
        type: "fill",
        filter: ["all", ["==", "meta", "feature"]],
        paint: {
            "fill-opacity": ["case", ["get", "user_is_active"], 0.5, 0.2],
            "fill-color": [
                "case",
                ["==", ["get", "user_plot_type"], 1],
                "#FF0000",
                "#00FF00",
            ],
        },
    },
    {
        id: "plot-label",
        type: "symbol",
        filter: ["all", ["==", "meta", "feature"]],
        layout: {
            "text-field": [
                "concat",
                [
                    "case",
                    ["==", ["get", "user_plot_type"], 1],
                    "Canary",
                    "Reference",
                ],
                "\n",
                " Pair Id: ",
                ["get", "user_pair_id"],
            ],
            "text-size": 10,
        },
    },
    {
        id: "plot-outline",
        type: "line",
        filter: ["all", ["==", "meta", "feature"]],
        paint: {
            "line-color": [
                "case",
                ["==", ["get", "user_flagged"], true],
                "yellow",
                "white",
            ],
            "line-width": ["case", ["==", ["get", "user_flagged"], true], 2, 0],
        },
    },
];

const AS_APPLIED_SOURCE = "insights-settings-as-applied-source";
const AS_APPLIED_LAYER: RasterLayerSpecification = {
    ...APPLICATION_OPERATION_IMAGE_LAYER,
    source: AS_APPLIED_SOURCE,
    id: "insights-settings-as-applied-layer",
    paint: {
        "raster-opacity": 0.8,
    },
    layout: {
        ...APPLICATION_OPERATION_IMAGE_LAYER.layout,
        visibility: "visible",
    },
};

function ActivePlots() {
    const { t } = useTranslation("insights");
    const draw = useRef<MapboxDraw | null>(null);
    const { map, mapLoaded } = useMap();
    const { fieldId } = useParams({
        from: "/dashboard/fields/$fieldId/insights/settings",
    });
    const { useGetRequest, useMutateRequest } = useRequest();
    const { fieldBbox } = useFieldDashboard() ?? {};
    const { userIsSentinel } = useAuth() ?? {};
    const { formatUnit } = useI18n();

    const COLUMNS = [
        { key: "type", label: t("tableColumns.type") },
        { key: "uploadType", label: t("tableColumns.creationType") },
        { key: "rxName", label: t("tableColumns.rxName") },
        { key: "date", label: t("tableColumns.dateLastApplied") },
        { key: "active", label: t("tableColumns.plotsActive") },
    ];

    const PLOT_COLUMNS = [
        { key: "pairId", label: t("tableColumns.pairId") },
        {
            key: "canaryRate",
            label: `${t("tableColumns.canary")} (${formatUnit("lb_ac", "kg_ha")})`,
        },
        {
            key: "referenceRate",
            label: `${t("tableColumns.reference")} (${formatUnit("lb_ac", "kg_ha")})`,
        },
        { key: "active", label: t("tableColumns.active") },
    ];

    useEffect(() => {
        if (map && mapLoaded && !draw.current) {
            draw.current = new MapboxDraw({
                displayControlsDefault: false,
                controls: {},
                defaultMode: "simple_select",
                userProperties: true,
                styles: DRAW_STYLES,
            });

            map.addControl(draw.current, "bottom");
        }
        return () => {
            if (map && draw.current) {
                if (map.hasControl(draw.current)) {
                    map.removeControl(draw.current);
                }
                draw.current = null;
            }
        };
    }, [map, mapLoaded]);

    const uploadedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/uploaded_plots/`,
        params: { boundary: "analytic" },
    });
    const appliedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/applied_plots/`,
        params: { boundary: "analytic" },
    });

    const plotSets = useMemo(() => {
        if (!uploadedPlots.isFetched || !appliedPlots.isFetched) return [];

        return [
            ...groupPlotsByCreatedDate(uploadedPlots.data),
            ...groupPlotsByPrescription(appliedPlots.data),
        ].map((plotset, index) => {
            return {
                key: index.toString(),
                type: plotset.isSlices
                    ? t("tableColumns.slices")
                    : t("tableColumns.blocks"),
                rxName: plotset.prescriptionName,
                date: plotset.latestAppliedDate?.isValid()
                    ? plotset.latestAppliedDate.format("ll")
                    : "--",
                uploadType: plotset.prescriptionId
                    ? t("tableColumns.rx")
                    : t("tableColumns.uploaded"),
                active: t("activeOfTotal", {
                    active: plotset.activePlots,
                    total: plotset.totalPlots,
                }),
                plots: plotset.plots,
            };
        });
    }, [
        appliedPlots.data,
        appliedPlots.isFetched,
        uploadedPlots.data,
        uploadedPlots.isFetched,
        t,
    ]);

    const [selectedPlots, setSelectedPlots] = useState<
        Feature<MultiPolygon, Plot>[]
    >([]);

    const [showAsApplied, setShowAsApplied] = useState(false);

    const hasAsApplied = useMemo(() => {
        return (
            selectedPlots.length > 0 &&
            !!selectedPlots[0]?.properties?.latest_application_id
        );
    }, [selectedPlots]);

    const hasConfirmation = useMemo(() => {
        return (
            selectedPlots.filter((plot) => plot.properties.confirmed).length > 0
        );
    }, [selectedPlots]);

    const imageQuery = useGetRequest({
        url: `application_operations/${[...selectedPlots][0]?.properties?.latest_application_id}/map_display_image/`,
        responseType: "blob",
        enabled: hasAsApplied,
    });

    const boundsQuery = useGetRequest<number[]>({
        url: `application_operations/${[...selectedPlots][0]?.properties?.latest_application_id}/bounds/`,
        enabled: hasAsApplied,
    });

    const asAppliedPlotsQuery = useGetRequest<
        FeatureCollection<MultiPolygon, AsAppliedPlot>
    >({
        url: `application_operations/${[...selectedPlots][0]?.properties?.latest_application_id}/as_applied_plots/`,
        enabled: hasAsApplied,
    });

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox || !hasAsApplied) return;

        const imageUrl = !imageQuery.data
            ? null
            : window.URL.createObjectURL(imageQuery.data as Blob);
        const bbox = boundsQuery.data
            ? (invertedTurfBboxToFourCornerCoordinates(
                  boundsQuery.data,
              ) as Coordinates)
            : fieldBbox;

        if (imageUrl && showAsApplied) {
            const imSource = map.getSource(AS_APPLIED_SOURCE) as ImageSource;
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: bbox,
                    });
                }
            } else {
                map.addSource(AS_APPLIED_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox,
                });
                map.addLayer({
                    id: AS_APPLIED_LAYER.id,
                    type: "raster",
                    source: AS_APPLIED_SOURCE,
                });
            }
        } else {
            removeLayersIfExists(map, [AS_APPLIED_LAYER.id]);
            removeSourcesIfExists(map, [AS_APPLIED_SOURCE]);
        }
    }, [
        hasAsApplied,
        imageQuery.data,
        fieldBbox,
        map,
        mapLoaded,
        showAsApplied,
        boundsQuery.data,
    ]);

    const onOpacityChange = useCallback(
        (value: number | number[]) => {
            if (!map) return;
            updateLayerOpacity(
                map,
                [AS_APPLIED_LAYER.id],
                Array.isArray(value) ? value[0] : value,
            );
        },
        [map],
    );

    const bringToFront = useCallback(() => {
        if (!draw.current || !mapLoaded || !map) return;

        const plots = draw.current.getAll();
        if (map.hasControl(draw.current)) {
            map.removeControl(draw.current);
        }
        map.addControl(draw.current);
        draw.current.add(plots);
    }, [map, mapLoaded]);

    const pairs = useMemo(() => {
        if (!selectedPlots.length) return [];

        return sortArrayByKey(
            selectedPlots.reduce<PlotTableRow[]>(
                (acc: PlotTableRow[], feature) => {
                    let existingRow = acc.find(
                        (row) => row.pairId === feature.properties.pair_id,
                    );

                    if (feature.properties.plot_type === PlotType.Canary) {
                        existingRow = {
                            ...(existingRow ?? {
                                pairId: feature.properties.pair_id,
                                referencePlot: undefined,
                            }),
                            canaryPlot: feature,
                        };
                    } else {
                        existingRow = {
                            ...(existingRow ?? {
                                pairId: feature.properties.pair_id,
                                canaryPlot: undefined,
                            }),
                            referencePlot: feature,
                        };
                    }

                    const newList = acc.filter(
                        (row) => row.pairId !== existingRow.pairId,
                    );
                    newList.push(existingRow);
                    return newList;
                },
                [],
            ) ?? [],
            "pairId",
        ).map((pair, index) => {
            return {
                key: index.toString(),
                pairId: pair.canaryPlot?.properties.pair_id,
                canaryRate: pair.canaryPlot?.properties.rate_offset ?? "--",
                referenceRate:
                    pair.referencePlot?.properties.rate_offset ?? "--",
                active:
                    pair.canaryPlot?.properties.is_active ??
                    pair.referencePlot?.properties.is_active,
                canary: pair.canaryPlot,
                reference: pair.referencePlot,
            };
        });
    }, [selectedPlots]);

    const onPlotChecked = useCallback((value: boolean, plotIds: number[]) => {
        if (!draw.current) return;
        const plots = draw.current?.getAll();
        const features = plots?.features.map((feature) => {
            if (feature.properties && plotIds.includes(feature.id as number)) {
                feature.properties.is_active = value;
            }

            return feature;
        });

        draw.current.deleteAll();
        draw.current.add({ type: "FeatureCollection", features });
        setSelectedPlots(features as any);
    }, []);

    const onPlotRateChange = useCallback(
        (plotId: number, plotType: PlotType, value: string | number) => {
            if (!draw.current) return;
            const plots = draw.current.getAll();
            const features = plots.features.map((feature) => {
                if (feature.id === plotId && feature.properties) {
                    let rateValue = Number(value);
                    if (plotType === PlotType.Canary && rateValue > 0) {
                        rateValue = -rateValue;
                    }
                    if (plotType === PlotType.Reference && rateValue < 0) {
                        rateValue = Math.abs(rateValue);
                    }
                    feature.properties.rate_offset = rateValue;
                }
                return feature;
            });

            draw.current.deleteAll();
            draw.current.add({ type: "FeatureCollection", features });
            setSelectedPlots(features as any);
        },
        [],
    );

    const onConfirmationChecked = useCallback((value: boolean) => {
        if (!draw.current) return;
        const plots = draw.current?.getAll();
        const features = plots?.features.map((feature) => {
            if (feature.properties) {
                feature.properties.confirmed = value;
            }

            return feature;
        });

        draw.current.deleteAll();
        draw.current.add({ type: "FeatureCollection", features });
        setSelectedPlots(features as any);
    }, []);

    const savePlotRequest = useMutateRequest({
        method: "post",
        url: "plots/update/",
        successMessage: t("plotsSavedSuccessfully"),
        errorMessage: t("failedToSavePlots"),
    });

    const savePlots = useCallback(() => {
        if (!draw.current) return;
        const features = draw.current.getAll();
        savePlotRequest.mutate(
            {
                data: features?.features.map((feature) => ({
                    ...feature.properties,
                    id: feature.id,
                    analytic_boundary: feature.geometry,
                })),
                params: { boundary: "analytic" },
            },
            {
                onSuccess: () => {
                    uploadedPlots.refetch();
                    appliedPlots.refetch();
                },
            },
        );
    }, [appliedPlots, savePlotRequest, uploadedPlots]);

    return (
        <div className="flex flex-col gap-2">
            <div className="font-semibold">
                {t("activeSentinelPlots", { ns: "insights" })}
            </div>
            <Table
                aria-label={t("ariaLabels.plots")}
                selectionMode="single"
                onSelectionChange={(e) => {
                    const key = [...e][0];
                    const plots =
                        plotSets?.find((set) => set.key == key)?.plots ?? [];
                    draw.current?.deleteAll();
                    draw.current?.add({
                        type: "FeatureCollection",
                        features: plots,
                    });
                    setSelectedPlots(plots);
                }}
            >
                <TableHeader columns={COLUMNS}>
                    {(column) => (
                        <TableColumn key={column.key}>
                            {column.label}
                        </TableColumn>
                    )}
                </TableHeader>
                <TableBody
                    items={plotSets}
                    emptyContent={t("messages.noPlotsFound")}
                >
                    {(item) => (
                        <TableRow key={item.key}>
                            {(columnKey) => (
                                <TableCell>
                                    {getKeyValue(item, columnKey)}
                                </TableCell>
                            )}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {!!pairs?.length && (
                <>
                    <div className="flex flex-row gap-2 justify-between">
                        <div className="flex flex-row gap-2 items-center text-sm">
                            {hasAsApplied && !imageQuery.isFetching && (
                                <>
                                    <Checkbox
                                        isSelected={showAsApplied}
                                        onValueChange={setShowAsApplied}
                                    >
                                        {t("asApplied")}
                                    </Checkbox>
                                    <Slider
                                        defaultValue={0.8}
                                        aria-label={t("ariaLabels.opacity")}
                                        className="w-[8rem]"
                                        onChange={onOpacityChange}
                                        minValue={0}
                                        maxValue={1}
                                        step={0.01}
                                        size="sm"
                                    />
                                </>
                            )}
                        </div>

                        <div>
                            <Button
                                color="primary"
                                variant="flat"
                                size="sm"
                                onPress={bringToFront}
                            >
                                {t("bringPlotsToTop")}
                            </Button>
                        </div>

                        {!!asAppliedPlotsQuery.data?.features?.length && (
                            <>
                                <div>
                                    <Button
                                        color="primary"
                                        variant="flat"
                                        size="sm"
                                        onPress={() => {
                                            draw.current?.deleteAll();
                                            draw.current?.add({
                                                type: "FeatureCollection",
                                                features:
                                                    selectedPlots.map(
                                                        (plot) => {
                                                            const asApplied =
                                                                asAppliedPlotsQuery.data?.features.find(
                                                                    (ap) =>
                                                                        ap
                                                                            .properties
                                                                            .plot ===
                                                                        plot.id,
                                                                );
                                                            return {
                                                                ...plot,
                                                                geometry:
                                                                    asApplied?.geometry ||
                                                                    plot.geometry,
                                                                properties: {
                                                                    ...plot.properties,
                                                                    flagged:
                                                                        asApplied
                                                                            ?.properties
                                                                            .flagged ||
                                                                        false,
                                                                },
                                                            };
                                                        },
                                                    ) ?? [],
                                            });
                                            bringToFront();
                                        }}
                                    >
                                        {t("autoCorrectPlots")}
                                    </Button>
                                </div>
                                <div>
                                    <Button
                                        color="primary"
                                        variant="flat"
                                        size="sm"
                                        onPress={() => {
                                            draw.current?.deleteAll();
                                            draw.current?.add({
                                                type: "FeatureCollection",
                                                features: selectedPlots,
                                            });
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faRotateLeft}
                                            className="mr-2"
                                        />
                                        {t("resetPlots")}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    <Table aria-label={t("ariaLabels.editPlots")}>
                        <TableHeader columns={PLOT_COLUMNS}>
                            {(column) => (
                                <TableColumn key={column.key}>
                                    {column.label}
                                </TableColumn>
                            )}
                        </TableHeader>
                        <TableBody items={pairs}>
                            {(item) => (
                                <TableRow key={item.key}>
                                    {(columnKey) => {
                                        if (columnKey === "canaryRate") {
                                            return (
                                                <TableCell>
                                                    {item.canary &&
                                                    item.canaryRate !== "--" ? (
                                                        <Input
                                                            type="number"
                                                            value={String(
                                                                item.canaryRate,
                                                            )}
                                                            onValueChange={(
                                                                value,
                                                            ) => {
                                                                onPlotRateChange(
                                                                    item.canary
                                                                        ?.id as number,
                                                                    PlotType.Canary,
                                                                    value,
                                                                );
                                                            }}
                                                            min={
                                                                userIsSentinel
                                                                    ? -90
                                                                    : -90
                                                            }
                                                            max={
                                                                userIsSentinel
                                                                    ? 0
                                                                    : -30
                                                            }
                                                            color="danger"
                                                            size="sm"
                                                        />
                                                    ) : (
                                                        "--"
                                                    )}
                                                </TableCell>
                                            );
                                        }

                                        if (columnKey === "referenceRate") {
                                            return (
                                                <TableCell>
                                                    {item.reference &&
                                                    item.referenceRate !==
                                                        "--" ? (
                                                        <Input
                                                            type="number"
                                                            value={String(
                                                                item.referenceRate,
                                                            )}
                                                            onValueChange={(
                                                                value,
                                                            ) => {
                                                                onPlotRateChange(
                                                                    item
                                                                        .reference
                                                                        ?.id as number,
                                                                    PlotType.Reference,
                                                                    value,
                                                                );
                                                            }}
                                                            min={
                                                                userIsSentinel
                                                                    ? 0
                                                                    : 30
                                                            }
                                                            max={90}
                                                            color="success"
                                                            size="sm"
                                                        />
                                                    ) : (
                                                        "--"
                                                    )}
                                                </TableCell>
                                            );
                                        }

                                        if (columnKey === "active") {
                                            const isActive = getKeyValue(
                                                item,
                                                columnKey,
                                            );
                                            return (
                                                <TableCell>
                                                    <Checkbox
                                                        isSelected={isActive}
                                                        onValueChange={(
                                                            selected,
                                                        ) => {
                                                            onPlotChecked(
                                                                selected,
                                                                [
                                                                    item.canary
                                                                        ?.id as number,
                                                                    item
                                                                        .reference
                                                                        ?.id as number,
                                                                ],
                                                            );
                                                        }}
                                                    />
                                                </TableCell>
                                            );
                                        }

                                        return (
                                            <TableCell>
                                                {getKeyValue(item, columnKey)}
                                            </TableCell>
                                        );
                                    }}
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <Checkbox
                        isSelected={hasConfirmation}
                        onValueChange={onConfirmationChecked}
                    >
                        {t("confirmPlotsAsAccurate", {
                            ns: "insights",
                        })}
                    </Checkbox>
                    <Button
                        className="self-end"
                        color="secondary"
                        variant="solid"
                        size="sm"
                        onPress={savePlots}
                        startContent={<FontAwesomeIcon icon={faSave} />}
                        isLoading={savePlotRequest.isPending}
                        fullWidth={true}
                    >
                        {t("save")}
                    </Button>
                </>
            )}
        </div>
    );
}

export default ActivePlots;
